/* eslint-disable react/no-danger */
import React from 'react';
import _, { isEmpty } from 'lodash';
import { motion } from 'framer-motion';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import BoxedContainer from './layouts/BoxedContainer';

function FreeZoneTwoColTextVideoOffset({ id, data }) {
  const title = _.get(data, 'title', '');
  const description = _.get(data, 'description', '');
  const descriptionCol = _.get(data, 'bodyDescription', '');
  const textCol1 = _.get(data, 'textCol1', '');
  const textCol2 = _.get(data, 'textCol2', '');
  const bgImage = _.get(data, 'bgImage', {});
  const mobileBgImage = _.get(data, 'mobileBgImage', {});
  const videoUrl = _.get(data, 'videoUrl', '');

  return (
    <section id={id} className="relative lg:-top-28 lg:-mb-28 z-50">
      <div className="md:pb-16 1xl:pb-[3vw] 2xl:pb-[7.5vw] 3xl-[pb-10vw] 4xl:pb-[12vw]
      max-w-[90%] w-[1410px] mx-auto">
        <div className="relative -top-20 -mb-20 overflow-hidden bg-[#f7f7f7]">
          {_.isEmpty(bgImage) || (
            <LazyLoadImage
              src={bgImage.url}
              alt={title}
              className="min-w-full min-h-full inset-0 object-cover absolute hidden md:block"
            />
          )}
          {_.isEmpty(mobileBgImage) || (
            <LazyLoadImage
              src={mobileBgImage.url}
              alt={title}
              className="min-w-full min-h-full inset-0 object-cover absolute block md:hidden"
            />
          )}
          <BoxedContainer variant="sm">
            <div
              className="w-full sm:px-6 pt-12.5 md:py-12.5 relative 
              z-10 md-pb-44 lg:pb-52 xl:pb-74 flex flex-col
            gap-5 md:gap-[50px] md:top-12.5">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                viewport={{ once: true }}
                className="flex flex-col gap-5 items-center">
                <div
                  className="child:text-clamp20to40 child:ltr:!font-URWDINMedium
                  child:rtl:!font-GESSTwoDWTCMedium child:leading-[1.3]
                  last:child:!m-0 child:text-center child:text-[#231F20]"
                  dangerouslySetInnerHTML={{ __html: title }}
                />
                <div className="py-px bg-red w-10 md:w-[70px] md:mb-1.25" />
                <div
                  className="child:text-clamp13to20 child:!font-LibreBaskervilleRegular
                  child:ltr:leading-[1.6] child:rtl:leading-[1.4] child:rtl:md:leading-[1.5] 
                  last:child:!m-0 child:text-center child:text-[#4D4D4D]"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              </motion.div>

              {!_.isEmpty(descriptionCol) && (
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.5 }}
                  viewport={{ once: true }}
                  dangerouslySetInnerHTML={{ __html: descriptionCol }}
                  className="child:text-clamp12to18 child:ltr:!font-URWDINRegular 
                  child:rtl:!font-GESSTwoDWTCLight child:leading-1.4
                  child:last-child:last-child:!m-0 child:text-center md:columns-2 md:gap-[35px] 
                  md:child:text-start child:text-[#4D4D4D]"
                />
              )}

              {_.isEmpty(descriptionCol) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.5 }}
                    viewport={{ once: true }}
                    className="child:text-clamp12to18 child:ltr:!font-URWDINRegular 
                    child:rtl:!font-GESSTwoDWTCLight child:leading-1.4
                  last:child:!m-0 child:text-center col-span-1 md:pb-10
                  md:child:text-start child:text-[#4D4D4D]
                  flex flex-col md:gap-y-5"
                    dangerouslySetInnerHTML={{ __html: textCol1 }}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.6 }}
                    viewport={{ once: true }}
                    className="child:text-clamp12to18 child:ltr:!font-URWDINRegular
                    child:rtl:!font-GESSTwoDWTCLight child:leading-1.4
                  last:child:!m-0 child:text-center col-span-1
                  md:child:text-start child:text-[#4D4D4D] pb-10 md:pb-0"
                    dangerouslySetInnerHTML={{ __html: textCol2 }}
                  />
                </div>
              )}
            </div>
          </BoxedContainer>
        </div>
      </div>
      {isEmpty(videoUrl) || (
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          viewport={{ once: true }}
          className="md:w-[calc(50%-16px)] overflow-hidden bg-[#f5f5f5]
          z-10 relative md:absolute md:bottom-0 rtl:md:left-0 ltr:md:right-0
          ">
          {videoUrl.toLowerCase().includes('.mp4') ? (
            <video
              muted
              playsInline
              loop
              autoPlay
              className="w-full h-full aspect-[818/460] 3xl:aspect-[900/505] object-cover">
                <source src={videoUrl} type="video/mp4" />
              </video>
          ) : (
            <iframe
              src={videoUrl}
              title="about intro video"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              webkitallowfullscreen="true"
              mozallowfullscreen="true"
              allowFullScreen
              className="w-full h-full aspect-[818/460] 3xl:aspect-[900/505]"
            />
          )}
        </motion.div>
      )}
    </section>
  );
}

export default FreeZoneTwoColTextVideoOffset;
